import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { EVENT_GENRE_OPTIONS } from '../constants/eventGenres';
import styles from './CreateEventPage.module.css';
import { useTranslation } from 'react-i18next';
import MapLocationPicker from '../components/MapLocationPicker';

const CreateEventPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation('createEvent');
  const draftPublicId = searchParams.get('draft'); // ?draft=evt_xxx
  const createEmptyArtist = () => ({ id: '', name: '', description: '', imageFile: null, imagePreview: '', existingImageUrl: '', existingImagePath: '', group_key: null, sort_order: 0 });

  const [form, setForm] = useState({
    name: '', date: '', end_date: '', open_time: '', start_time: '', end_time: '',
    slug: '', genre: '', address: '', price: '', description: '', contact_info: '', venue_id: '', venue_name: '', fee_bearer: 'organizer',
    event_type: 'paid', donation_enabled: false, donation_start_at: '', donation_end_at: '',
    lat: '', lng: '',
  });

  const [artists, setArtists] = useState([createEmptyArtist()]);
  const [performerGroups, setPerformerGroups] = useState([]); // {key, id, name, sort_order}
  const [flyers, setFlyers] = useState([]); // {file, previewUrl}[]
  const [draftFlyers, setDraftFlyers] = useState([]); // ドラフト読み込み済みフライヤー {id, url}[]
  const [deletingDraftFlyerId, setDeletingDraftFlyerId] = useState(null);
  const flyerInputRef = useRef(null);
  const [venueSearch, setVenueSearch] = useState('');
  const [venueResults, setVenueResults] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [selectedArtist, setSelectedArtist] = useState(null);

  const getFieldError = (...names) => {
    for (const name of names) {
      const msg = fieldErrors?.[name]?.[0];
      if (msg) return msg;
    }
    return '';
  };

  const getErrorSummary = () => {
    const messages = Object.values(fieldErrors || {}).flat().map((msg) => String(msg));
    if (messages.length === 0) return '';
    return Array.from(new Set(messages)).join(' / ');
  };

  const humanizeValidationMessage = (key, message) => {
    const msg = String(message || '');
    if (key === 'contact_info') {
      if (msg.includes('required')) return t('errContactRequired');
      return t('errContactCheck');
    }
    if (/^artist_names\.\d+$/.test(key)) {
      if (msg.includes('required')) return t('errArtistNameRequired');
      return t('errArtistNameCheck');
    }
    if (/^artist_images\.\d+$/.test(key)) return t('errArtistImage');
    if (/^flyers\.\d+$/.test(key)) return t('errFlyer');
    if (key === 'price_groups') return t('errPriceGroups');
    if (/^price_groups\.\d+\.type$/.test(key)) return t('errGroupType');
    if (/^price_groups\.\d+\.prices\.\d+\.price_type$/.test(key)) return t('errPriceType');
    if (/^price_groups\.\d+\.prices\.\d+\.price$/.test(key)) return t('errPrice');
    if (/^price_groups\.\d+\.prices\.\d+\.ticket_limit$/.test(key)) return t('errTicketLimit');
    return msg;
  };

  const clearFieldError = (...names) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      names.forEach((name) => {
        if (next[name]) {
          delete next[name];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const isPastEvent = (dateValue, endDateValue) => {
    const referenceDate = endDateValue || dateValue;
    if (!referenceDate) return false;
    return new Date(referenceDate) < new Date(new Date().toDateString());
  };

  const getDisplayValue = (value, fallback = t('fallbackNoInput')) => {
    const text = String(value ?? '').trim();
    return text || fallback;
  };

  const getArtistSummary = () => {
    const names = artists
      .map((artist) => String(artist?.name || '').trim())
      .filter(Boolean);
    return names.length > 0 ? names.join(' / ') : t('artistNotSet');
  };

  const getTicketSummary = () => {
    if (form.event_type === 'free') {
      return [t('ticketFree')];
    }

    const lines = ticketPrices
      .filter((group) => String(group?.type || '').trim() !== '' || (group?.prices || []).length > 0)
      .map((group) => {
        const groupName = getDisplayValue(group?.type, t('ticketGroupUnnamed'));
        const priceNames = (group?.prices || [])
          .map((price) => {
            const type = getDisplayValue(price?.price_type, t('ticketTypeUnnamed'));
            const amount = String(price?.price ?? '').trim();
            return amount !== '' ? `${type} ${amount}${t('ticketSuffix')}` : type;
          })
          .join(' / ');
        return priceNames ? `${groupName}: ${priceNames}` : groupName;
      });

    return lines.length > 0 ? lines : [t('fallbackNoValue')];
  };

  const getConfirmSections = () => ([
    { label: t('confirmEventName'), value: getDisplayValue(form.name) },
    { label: t('confirmGenre'), value: getDisplayValue(form.genre) },
    { label: t('confirmDate'), value: getDisplayValue(form.date) },
    { label: t('confirmEndDate'), value: getDisplayValue(form.end_date) },
    { label: t('confirmTime'), value: `${getDisplayValue(form.open_time)} / ${getDisplayValue(form.start_time)} / ${getDisplayValue(form.end_time)}` },
    { label: t('confirmVenue'), value: getDisplayValue(form.venue_name) },
    { label: t('confirmAddress'), value: getDisplayValue(form.address) },
    { label: t('confirmContact'), value: getDisplayValue(form.contact_info) },
    { label: t('confirmEventType'), value: form.event_type === 'free' ? t('confirmEventTypeFree') : t('confirmEventTypePaid') },
    { label: t('confirmDonation'), value: form.donation_enabled ? `${t('confirmDonationEnabled')}${form.donation_start_at ? t('confirmDonationStart', { date: form.donation_start_at }) : ''}` : t('confirmDonationDisabled') },
    { label: t('confirmTickets'), value: getTicketSummary().join('\n') },
    { label: t('confirmArtists'), value: getArtistSummary() },
    { label: t('confirmFlyers'), value: t('confirmFlyerCount', { count: draftFlyers.length + flyers.length }) },
  ]);

  const validateBeforePublish = () => {
    setFieldErrors({});
    setSuccess('');

    if ((!flyers || flyers.length === 0) && (!draftFlyers || draftFlyers.length === 0)) {
      setFieldErrors({ flyers: [t('errFlyerRequired')] });
      return false;
    }

    if (!form.slug || !form.slug.trim()) {
      setFieldErrors({ slug: [t('errSlugRequired')] });
      return false;
    }

    if (form.event_type !== 'free') {
      if (!ticketPrices || ticketPrices.length === 0) {
        setFieldErrors({ price_groups: [t('errTicketRequired')] });
        return false;
      }
      const hasAtLeastOneType = ticketPrices.some((tp) => String(tp?.type || '').trim() !== '');
      if (!hasAtLeastOneType) {
        setFieldErrors({ 'price_groups.0.type': [t('errGroupTypeRequired')] });
        return false;
      }
    }

    return true;
  };

  const submitPublish = async () => {
    setIsSubmitting(true);
    try {
      const formData = buildFormData(false, !!draftId);
      if (draftId) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/events/${draftId}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/events`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setIsConfirmOpen(false);
      setSuccess(t('successPublish'));
    } catch (err) {
      console.error('event registration error:', err);
      const data = err?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        const normalizedFieldErrors = {};
        Object.entries(data.errors).forEach(([key, value]) => {
          const rawMessages = Array.isArray(value) ? value : [value];
          const messages = rawMessages.map((msg) => humanizeValidationMessage(key, msg));
          normalizedFieldErrors[key] = messages;
        });
        setFieldErrors(normalizedFieldErrors);
      } else if (data?.message) {
        setFieldErrors({ submit: [String(data.message)] });
      } else {
        setFieldErrors({ submit: [t('errSubmit')] });
      }
      setIsConfirmOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };



    // 新しい価格帯管理（各期間ごとに複数価格タイプを持つ）
    // price_groups: 期間（グループ） + 価格タイプのネスト構造
    const [ticketPrices, setTicketPrices] = useState([
      {
        type: '超早期購入',
        price_date: '',
        price_end_date: '',
        ticket_limit: '', // グループ(期間)単位の上限
        prices: [
          { price_type: '通常', price: '', ticket_limit: '' }
        ]
      }
    ]);

    const [enableGenderPricing, setEnableGenderPricing] = useState(false);
    const [enableGroupDiscount, setEnableGroupDiscount] = useState(false);
    const [regularPrice, setRegularPrice] = useState('');
    const [malePrice, setMalePrice] = useState('');
    const [femalePrice, setFemalePrice] = useState('');
    const [groupPrices, setGroupPrices] = useState([{ min_people: '', price: '' }]);
    const [enableVIP, setEnableVIP] = useState(false);
    const [vipPrice, setVIPPrice] = useState('');
    const [vipLimit, setVipLimit] = useState('');
    const [flyerPreviews, setFlyerPreviews] = useState([]); // unused - kept for compat
    const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftId, setDraftId] = useState(null); // 編集中の下書きpublic_id
  const [draftNote, setDraftNote] = useState('');
  const [success, setSuccess] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    if (!success) return undefined;
    const timerId = setTimeout(() => {
      navigate('/dashboard');
    }, 2500);

    return () => clearTimeout(timerId);
  }, [success, navigate]);

  // ?draft=evt_xxx が指定されていれば下書きデータをフォームに復元
  useEffect(() => {
    if (!draftPublicId) return;
    axios.get(`${process.env.REACT_APP_API_URL}/me/events/${draftPublicId}/draft`)
      .then((res) => {
        const d = res.data;
        setDraftId(d.id);
        setForm({
          name:               d.name               || '',
          genre:              d.genre              || '',
          slug:               d.slug               || '',
          date:               d.date               || '',
          end_date:           d.end_date           || '',
          open_time:          d.open_time          || '',
          start_time:         d.start_time         || '',
          end_time:           d.end_time           || '',
          address:            d.address            || '',
          description:        d.description        || '',
          contact_info:       d.contact_info       || '',
          venue_id:           d.venue_id           || '',
          venue_name:         d.venue_name         || '',
          fee_bearer:         d.fee_bearer         || 'organizer',
          price:              '',
          event_type:         d.event_type         || 'paid',
          donation_enabled:   d.donation_enabled   ?? false,
          donation_start_at:  d.donation_start_at  ? String(d.donation_start_at).slice(0, 16) : '',
          donation_end_at:    d.donation_end_at    ? String(d.donation_end_at).slice(0, 16)   : '',
          lat:                d.lat != null ? String(d.lat) : '',
          lng:                d.lng != null ? String(d.lng) : '',
        });
        if (Array.isArray(d.artists) && d.artists.length > 0) {
          setArtists(d.artists.map((a) => ({
            ...createEmptyArtist(),
            id: a.id || '',
            name: a.name || '',
            description: a.description || '',
            existingImageUrl: a.image_url || '',
            existingImagePath: a.image_path || '',
            group_key: a.group_key || null,
            sort_order: a.sort_order ?? 0,
          })));
        }
        if (Array.isArray(d.performer_groups)) {
          setPerformerGroups(d.performer_groups.map((g) => ({
            key: g.key || ('group-' + g.id),
            id: g.id || null,
            name: g.name || '',
            sort_order: g.sort_order ?? 0,
          })));
        }
        if (Array.isArray(d.price_groups) && d.price_groups.length > 0) {
          setTicketPrices(d.price_groups.map((g) => ({
            type:           g.type           || '',
            price_date:     g.price_date     || '',
            price_end_date: g.price_end_date || '',
            ticket_limit:   g.ticket_limit   || '',
            prices: Array.isArray(g.prices) && g.prices.length > 0
              ? g.prices.map((p) => ({
                  price_type:   p.price_type   || '通常',
                  price:        p.price        || '',
                  ticket_limit: p.ticket_limit || '',
                  min_people:   p.min_people   || '',
                }))
              : [{ price_type: '通常', price: '', ticket_limit: '' }],
          })));
        }
        // アップロード済みフライヤーはプレビューとして表示
        if (Array.isArray(d.flyers) && d.flyers.length > 0) {
          setDraftFlyers(d.flyers.map((f) => ({ id: f.id, url: f.url })));
        }
        setDraftNote(t('successDraftLoad'));
      })
      .catch(() => setDraftNote(t('errDraftLoad')));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftPublicId]);

  const handleGroupChange = (index, field, value) => {
    const updated = [...groupPrices];
    updated[index][field] = value;
    setGroupPrices(updated);
  };

  const addGroupField = () => {
    setGroupPrices([...groupPrices, { min_people: '', price: '' }]);
  };

  const removeGroupField = (index) => {
    const updated = [...groupPrices];
    updated.splice(index, 1);
    setGroupPrices(updated);
  };

  const resizeFlyerIfNeeded = async (file) => {
    const MAX = 3000;
    try {
      const bitmap = await createImageBitmap(file);
      const { width: w, height: h } = bitmap;
      if (w <= MAX && h <= MAX) {
        bitmap.close();
        return file;
      }
      const scale = MAX / Math.max(w, h);
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) { bitmap.close(); return file; }
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();
      const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      return await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name, { type: mimeType, lastModified: file.lastModified }));
        }, mimeType, 0.92);
      });
    } catch (e) {
      console.warn('[flyer resize] failed, using original:', e);
      return file;
    }
  };

  const handleFlyerChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    clearFieldError('flyers');
    const resized = await Promise.all(files.map(resizeFlyerIfNeeded));
    const newEntries = resized.map(file => ({ file, previewUrl: URL.createObjectURL(file) }));
    setFlyers(prev => [...prev, ...newEntries]);
    if (flyerInputRef.current) flyerInputRef.current.value = '';
  };

  const handleRemoveFlyer = (idx) => {
    setFlyers(prev => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleDeleteDraftFlyer = async (id) => {
    setDeletingDraftFlyerId(id);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/event-images/${id}`);
      setDraftFlyers(prev => prev.filter(f => f.id !== id));
    } catch (err) {
      alert(t('errFlyerDelete'));
    } finally {
      setDeletingDraftFlyerId(null);
    }
  };
  

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);

  useEffect(() => {
  if (!venueSearch || typeof venueSearch !== 'string' || venueSearch.trim() === '') return;
    axios.get(`${process.env.REACT_APP_API_URL}/search-venues?query=${venueSearch}`)
      .then(res => setVenueResults(res.data))
      .catch(() => setVenueResults([]));
  }, [venueSearch]);

  useEffect(() => {
    if (artistSearch.trim() === '') return;
    axios.get(`${process.env.REACT_APP_API_URL}/search-users?role=artist&query=${artistSearch}`)
      .then(res => setArtistResults(res.data))
      .catch(() => setArtistResults([]));
  }, [artistSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'venue_name') {
      setForm(prev => ({ ...prev, venue_name: value, venue_id: '' }));
    } else if (name === 'date' || name === 'end_date') {
      const nextDate = name === 'date' ? value : form.date;
      const nextEndDate = name === 'end_date' ? value : form.end_date;
      const isPast = isPastEvent(nextDate, nextEndDate);
      setForm(prev => ({
        ...prev,
        [name]: value,
        ...(isPast ? { event_type: 'free', donation_enabled: false } : {}),
      }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
    clearFieldError(name, 'submit');
  };



  // 価格帯（期間）追加・削除・編集
  const handleTicketPriceChange = (idx, field, value) => {
    setTicketPrices(prices => prices.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };
  const addTicketPrice = () => {
    setTicketPrices(prices => [...prices, {
      type: '',
      price_date: '',
      price_end_date: '',
      ticket_limit: '',
      prices: [ { price_type: '通常', price: '', ticket_limit: '' } ]
    }]);
  };
  const removeTicketPrice = (idx) => {
    setTicketPrices(prices => prices.filter((_, i) => i !== idx));
  };

  // 価格タイプ追加・削除・編集（各期間ごと）
  const handlePriceTypeChange = (periodIdx, priceIdx, field, value) => {
    setTicketPrices(prices => prices.map((p, i) =>
      i === periodIdx
        ? { ...p, prices: p.prices.map((pt, j) => j === priceIdx ? { ...pt, [field]: value } : pt) }
        : p
    ));
  };
  const addPriceType = (periodIdx, price_type) => {
    setTicketPrices(prices => prices.map((p, i) =>
      i === periodIdx
        ? { ...p, prices: [...p.prices, { price_type, price: '', ticket_limit: '', min_people: '' }] }
        : p
    ));
  };
  const removePriceType = (periodIdx, priceIdx) => {
    setTicketPrices(prices => prices.map((p, i) =>
      i === periodIdx
        ? { ...p, prices: p.prices.filter((_, j) => j !== priceIdx) }
        : p
    ));
  };

  const addArtistToList = (artist) => {
    if (!artist) return;
    if (artist.id && artists.some((a) => a.id === artist.id)) {
      return;
    }
    const normalizedArtist = {
      ...createEmptyArtist(),
      ...artist,
    };
    {
      setArtists(prev => {
        const copy = [...prev];
        const emptyIndex = copy.findIndex(a => !a.name || (typeof a.name === 'string' && a.name.trim() === ''));

        if (emptyIndex !== -1) {
          copy[emptyIndex] = normalizedArtist;
        } else {
          copy.push(normalizedArtist);
        }

        return copy;
      });
    }
  };

  const handleArtistValueChange = (index, field, value) => {
    setArtists(prev => prev.map((artist, i) => {
      if (i !== index) return artist;

      if (field === 'name') {
        return {
          ...artist,
          name: value,
          id: '',
          existingImageUrl: artist.id ? '' : artist.existingImageUrl,
        };
      }

      return {
        ...artist,
        [field]: value,
      };
    }));
  };

  const handleArtistImageChange = (index, file) => {
    setArtists(prev => prev.map((artist, i) => {
      if (i !== index) return artist;

      return {
        ...artist,
        imageFile: file || null,
        imagePreview: file ? URL.createObjectURL(file) : '',
      };
    }));
  };

  const buildFormData = (isDraft, isUpdate = false) => {
    const formData = new FormData();
    if (isDraft) formData.append('is_draft', 'true');
    if (isUpdate) formData.append('_method', 'PUT');
    Object.entries(form).forEach(([key, value]) => {
      if (key === 'price') {
        formData.append(key, value || 0);
      } else if (key === 'donation_enabled') {
        formData.append(key, value ? '1' : '0');
      } else {
        formData.append(key, value ?? '');
      }
    });
    flyers.forEach(({ file }) => {
      formData.append('flyers[]', file);
    });
    performerGroups.forEach((g, i) => {
      formData.append(`performer_groups[${i}][key]`, g.key);
      formData.append(`performer_groups[${i}][name]`, g.name);
      formData.append(`performer_groups[${i}][sort_order]`, g.sort_order ?? i);
    });
    artists.forEach((artist, i) => {
      formData.append(`artist_ids[${i}]`, artist.id || '');
      formData.append(`artist_names[${i}]`, artist.name);
      formData.append(`artist_descriptions[${i}]`, artist.id ? '' : (artist.description || ''));
      formData.append(`artist_existing_image_paths[${i}]`, artist.id ? '' : (artist.existingImagePath || ''));
      formData.append(`artist_group_keys[${i}]`, artist.group_key || '');
      formData.append(`artist_sort_orders[${i}]`, artist.sort_order ?? i);
      if (!artist.id && artist.imageFile) {
        formData.append(`artist_images[${i}]`, artist.imageFile);
      }
    });
    if (form.event_type !== 'free') {
      ticketPrices.forEach((tp, i) => {
        formData.append(`price_groups[${i}][type]`, tp.type);
        formData.append(`price_groups[${i}][price_date]`, tp.price_date);
        formData.append(`price_groups[${i}][price_end_date]`, tp.price_end_date);
        formData.append(`price_groups[${i}][ticket_limit]`, tp.ticket_limit || '');
        tp.prices.forEach((pt, j) => {
          formData.append(`price_groups[${i}][prices][${j}][price_type]`, pt.price_type);
          formData.append(`price_groups[${i}][prices][${j}][price]`, pt.price);
          formData.append(`price_groups[${i}][prices][${j}][ticket_limit]`, pt.ticket_limit || '');
          if (pt.price_type === 'グループ') {
            formData.append(`price_groups[${i}][prices][${j}][min_people]`, pt.min_people || '');
          }
        });
      });
    }
    return formData;
  };

  const handleSaveDraft = async () => {
    if (isSavingDraft || isSubmitting) return;
    if (!form.name.trim()) {
      setFieldErrors({ name: [t('errDraftNameRequired')] });
      return;
    }
    if (!form.slug.trim()) {
      setFieldErrors({ slug: [t('errDraftSlugRequired')] });
      return;
    }
    setIsSavingDraft(true);
    setFieldErrors({});
    try {
      const formData = buildFormData(true, !!draftId);
      let res;
      if (draftId) {
        // 既存下書きを更新
        res = await axios.post(
          `${process.env.REACT_APP_API_URL}/events/${draftId}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        res = await axios.post(`${process.env.REACT_APP_API_URL}/events`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setDraftId(res.data.draft_id);
      }
      navigate('/dashboard');
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) {
        const normalized = {};
        Object.entries(data.errors).forEach(([key, value]) => {
          const rawMessages = Array.isArray(value) ? value : [value];
          normalized[key] = rawMessages.map((msg) => humanizeValidationMessage(key, msg));
        });
        setFieldErrors(normalized);
      } else {
        setDraftNote(data?.message || t('errDraftSave'));
      }
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validateBeforePublish()) {
      return;
    }

    setIsConfirmOpen(true);
  };




  return (
    <>
      <Header user={user} />
      <div className={styles.container}>
        <h2 className={styles.heading}>{t('heading')}</h2>

        {isConfirmOpen && (
          <div className={styles.confirmOverlay} onClick={() => !isSubmitting && setIsConfirmOpen(false)}>
            <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('confirmAriaLabel')}>
              <h3 className={styles.confirmTitle}>{t('confirmTitle')}</h3>
              <p className={styles.confirmText}>{t('confirmText')}</p>
              <div className={styles.confirmList}>
                {getConfirmSections().map((section) => (
                  <div key={section.label} className={styles.confirmItem}>
                    <div className={styles.confirmLabel}>{section.label}</div>
                    <div className={styles.confirmValue}>{section.value}</div>
                  </div>
                ))}
              </div>
              <div className={styles.confirmActions}>
                <button type="button" className={styles.secondarySubmitButton} onClick={() => setIsConfirmOpen(false)} disabled={isSubmitting}>{t('confirmBack')}</button>
                <button type="button" onClick={submitPublish} disabled={isSubmitting}>{isSubmitting ? t('confirmPublishing') : t('confirmPublish')}</button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <fieldset>
            <legend>{t('legendBasic')}</legend>
            <label>
              {t('labelEventName')}<input type="text" name="name" value={form.name} onChange={handleChange} className={getFieldError('name') ? styles.inputError : ''} required />
            </label>
            {getFieldError('name') && <p className={styles.fieldError}>{getFieldError('name')}</p>}
            <label>
              {t('labelGenre')}
              <input
                type="text"
                name="genre"
                value={form.genre}
                onChange={handleChange}
                list="event-genre-options"
                placeholder={t('placeholderGenre')}
                className={getFieldError('genre') ? styles.inputError : ''}
              />
              <datalist id="event-genre-options">
                {EVENT_GENRE_OPTIONS.map((genre) => (
                  <option key={genre} value={genre} />
                ))}
              </datalist>
            </label>
            {getFieldError('genre') && <p className={styles.fieldError}>{getFieldError('genre')}</p>}
            <label>
              {t('labelSlug')}
              <input
                type="text"
                name="slug"
                value={form.slug}
                onChange={handleChange}
                placeholder={t('placeholderSlug')}
                pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
                title={t('noteSlug')}
                className={getFieldError('slug') ? styles.inputError : ''}
                required={!draftId}
              />
              {getFieldError('slug') && <p className={styles.fieldError}>{getFieldError('slug')}</p>}
              <span style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '2px' }}>
                {t('noteSlug')}
              </span>
            </label>
      <label className={styles.inputLabel}>
        {t('labelFlyer')}
      </label>
      <input
        type="file"
        multiple
        accept="image/*"
        onChange={handleFlyerChange}
        ref={flyerInputRef}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        onClick={() => flyerInputRef.current && flyerInputRef.current.click()}
        style={{ border: '1px solid #bbb', background: '#fff', color: '#333', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
      >
        {t('labelFlyerSelect')}
      </button>
      {getFieldError('flyers') && <p className={styles.fieldError}>{getFieldError('flyers')}</p>}

      {/* ドラフト読み込み済みフライヤー（即時削除可） */}
      {draftFlyers.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 6px' }}>{t('noteFlyerPreview')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 110px)', gap: 10 }}>
            {draftFlyers.map((flyer) => (
              <div key={flyer.id} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 6 }}>
                <img src={flyer.url} alt="flyer" style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 4 }} />
                <button
                  type="button"
                  onClick={() => handleDeleteDraftFlyer(flyer.id)}
                  disabled={deletingDraftFlyerId === flyer.id}
                  style={{ marginTop: 4, width: '100%', border: '1px solid #dc2626', background: '#fff', color: '#dc2626', borderRadius: 6, padding: '3px 0', fontWeight: 600, cursor: deletingDraftFlyerId === flyer.id ? 'not-allowed' : 'pointer', fontSize: 12 }}
                >
                  {deletingDraftFlyerId === flyer.id ? t('deletingFlyer') : t('deleteFlyer')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 新規選択フライヤー（個別削除可） */}
      {flyers.length > 0 && (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 110px)', gap: 10 }}>
          {flyers.map((entry, idx) => (
            <div key={idx} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 6 }}>
              <img src={entry.previewUrl} alt={`new flyer ${idx + 1}`} style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 4 }} />
              <button
                type="button"
                onClick={() => handleRemoveFlyer(idx)}
                style={{ marginTop: 4, width: '100%', border: '1px solid #bbb', background: '#fff', color: '#555', borderRadius: 6, padding: '3px 0', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
              >
                {t('cancelPendingFlyer')}
              </button>
            </div>
          ))}
        </div>
      )}
          </fieldset>
          <fieldset>
            <legend>{t('legendVenue')}</legend>
            <label>
              {t('labelVenueSearch')}
              <span style={{ fontSize: '12px', color: '#888', display: 'block', marginTop: '2px' }}>
                {t('noteVenueSearch')}
              </span>
              <input type="text" value={venueSearch} onChange={(e) => setVenueSearch(e.target.value)} />
            </label>
            {venueResults.length > 0 && (
              <ul className={styles.searchResults}>
                {venueResults.map((v) => (
                  <li key={v.id} onClick={() => setForm({ ...form, venue_id: v.id, venue_name: v.name, address: v.address })}>
                    {v.name} ({v.address})
                  </li>
                ))}
              </ul>
            )}
            <label>
              {t('labelVenueName')}<input type="text" name="venue_name" value={form.venue_name} onChange={handleChange} className={getFieldError('venue_name') ? styles.inputError : ''} />
            </label>
            {getFieldError('venue_name') && <p className={styles.fieldError}>{getFieldError('venue_name')}</p>}
            <label>
              {t('labelAddress')}<input type="text" name="address" value={form.address} onChange={handleChange} className={getFieldError('address') ? styles.inputError : ''} required />
            </label>
            {getFieldError('address') && <p className={styles.fieldError}>{getFieldError('address')}</p>}
            {!form.venue_id && (
              <div>
                <label style={{ fontWeight: 500 }}>イベント会場の位置（任意）</label>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 4px' }}>登録済み会場を使わない場合、会場イベントMAPに表示する位置を設定できます</p>
                <MapLocationPicker
                  lat={form.lat}
                  lng={form.lng}
                  onChange={(lat, lng) => setForm(prev => ({ ...prev, lat, lng }))}
                />
              </div>
            )}
          </fieldset>

          <fieldset>
            <legend>{t('legendDate')}</legend>
            <label>
              {t('labelDate')}<input type="date" name="date" value={form.date} onChange={handleChange} className={getFieldError('date') ? styles.inputError : ''} required />
            </label>
            {getFieldError('date') && <p className={styles.fieldError}>{getFieldError('date')}</p>}
            <label>
              {t('labelEndDate')}<input type="date" name="end_date" value={form.end_date} onChange={handleChange} className={getFieldError('end_date') ? styles.inputError : ''} required />
            </label>
            {getFieldError('end_date') && <p className={styles.fieldError}>{getFieldError('end_date')}</p>}
            <label>
              {t('labelOpenTime')}<input type="time" name="open_time" value={form.open_time} onChange={handleChange} className={getFieldError('open_time') ? styles.inputError : ''} required />
            </label>
            {getFieldError('open_time') && <p className={styles.fieldError}>{getFieldError('open_time')}</p>}
            <label>
              {t('labelStartTime')}<input type="time" name="start_time" value={form.start_time} onChange={handleChange} className={getFieldError('start_time') ? styles.inputError : ''} required />
            </label>
            {getFieldError('start_time') && <p className={styles.fieldError}>{getFieldError('start_time')}</p>}
            <label>
              {t('labelEndTime')}<input type="time" name="end_time" value={form.end_time} onChange={handleChange} className={getFieldError('end_time') ? styles.inputError : ''} required />
            </label>
            {getFieldError('end_time') && <p className={styles.fieldError}>{getFieldError('end_time')}</p>}
          </fieldset>
          {/* イベント全体販売枚数フィールド削除（グループ単位へ移行） */}

          <fieldset>
            <legend>{t('legendEventType')}</legend>
            {isPastEvent(form.date, form.end_date) && (
              <p style={{ color: '#b45309', fontSize: '13px', marginBottom: '8px' }}>
                {t('notePastEvent')}
              </p>
            )}
            <label>
              {t('labelEventType')}
              <select
                name="event_type"
                value={form.event_type}
                onChange={(e) => { handleChange(e); }}
                disabled={isPastEvent(form.date, form.end_date)}
              >
                <option value="paid">{t('optionPaid')}</option>
                <option value="free">{t('optionFree')}</option>
              </select>
            </label>

            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="donation_enabled"
                  checked={!!form.donation_enabled}
                  disabled={isPastEvent(form.date, form.end_date)}
                  onChange={(e) => setForm(prev => ({ ...prev, donation_enabled: e.target.checked }))}
                />
                {t('labelDonationEnabled')}
              </label>
              <span style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '2px' }}>
                {t('noteDonation')}
              </span>
            </div>

            {form.donation_enabled && (
              <div style={{ marginTop: '12px', paddingLeft: '8px', borderLeft: '3px solid #c7d2fe' }}>
                <label>
                  {t('labelDonationStart')}
                  <input
                    type="datetime-local"
                    name="donation_start_at"
                    value={form.donation_start_at}
                    onChange={handleChange}
                  />
                </label>
                <div style={{ marginTop: '8px', display: 'block', color: '#444' }}>
                  {t('noteDonationEndAuto')}
                </div>
              </div>
            )}
          </fieldset>




    <fieldset>
      <legend>{t('legendTickets')}</legend>
      {form.event_type === 'free' ? (
        <p style={{ color: '#16a34a', fontWeight: '600' }}>
          {t('freeEventNote')}
        </p>
      ) : (
        <>
      <label>
        {t('labelFeeBearer')}
        <select name="fee_bearer" value={form.fee_bearer} onChange={handleChange} className={getFieldError('fee_bearer') ? styles.inputError : ''}>
          <option value="organizer">{t('optionOrganizer')}</option>
          <option value="buyer">{t('optionBuyer')}</option>
        </select>
      </label>
      {getFieldError('fee_bearer') && <p className={styles.fieldError}>{getFieldError('fee_bearer')}</p>}
      {ticketPrices.map((tp, idx) => (
        <div key={idx} className={styles.ticketGroupCard}>
          <div className={styles.ticketGroupHeader}>
            <strong>{t('ticketGroupTitle', { num: idx + 1 })}</strong>
            <button type="button" onClick={() => removeTicketPrice(idx)} className={styles.dangerButton}>{t('ticketGroupDelete')}</button>
          </div>

          <label>
            {t('ticketGroupType')}
            <select
              value={tp.type === '' ? 'その他' : (['超早期購入','早期購入','通常購入'].includes(tp.type) ? tp.type : 'その他')}
              onChange={e => {
                if (e.target.value === 'その他') {
                  handleTicketPriceChange(idx, 'type', '');
                } else {
                  handleTicketPriceChange(idx, 'type', e.target.value);
                }
              }}
            >
              <option value="">{t('optionSelectDefault')}</option>
              <option value="超早期購入">{t('optionTypeUltraEarly')}</option>
              <option value="早期購入">{t('optionTypeEarly')}</option>
              <option value="通常購入">{t('optionTypeNormal')}</option>
              <option value="その他">{t('optionTypeOther')}</option>
            </select>
            {/* "その他"または空文字列のときは必ずテキスト入力欄を表示 */}
            {(tp.type === '' || !['超早期購入','早期購入','通常購入'].includes(tp.type)) && (
              <>
                <input
                  type="text"
                  value={tp.type}
                  maxLength={30}
                  onChange={e => {
                    const val = e.target.value;
                    // <, > 禁止
                    if (/[<>]/.test(val)) return;
                    handleTicketPriceChange(idx, 'type', val);
                  }}
                placeholder={t('placeholderTicketGroupType')}
                  className={styles.inlineTypeInput}
                />
                {tp.type.length > 0 && /[<>]/.test(tp.type) && (
                  <span className={styles.inlineErrorText}>{t('noteInvalidChars')}</span>
                )}
              </>
            )}
          </label>
            <label>
              {t('ticketGroupSaleLimit')}
              <input
                type="number"
                value={tp.ticket_limit}
                onChange={e => handleTicketPriceChange(idx, 'ticket_limit', e.target.value)}
                placeholder={t('placeholderUnlimited')}
              />
            </label>
          <div className={styles.ticketMetaGrid}>
            <label>
              {t('ticketGroupSaleStart')}
              <input type="datetime-local" value={tp.price_date} onChange={e => handleTicketPriceChange(idx, 'price_date', e.target.value)} />
            </label>
            <label>
              {t('ticketGroupSaleEnd')}
              <input type="datetime-local" value={tp.price_end_date} onChange={e => handleTicketPriceChange(idx, 'price_end_date', e.target.value)} />
            </label>
          </div>

          {/* 価格タイプごとにネスト + グループ上限 */}
          <div className={styles.priceTypeArea}>
            {tp.prices.map((pt, j) => (
              <div key={j} className={styles.priceTypeRow}>
                <label>
                  {t('priceTypeLabel')}
                  <select
                    value={pt.price_type === '' ? 'other' : (['通常','男性','女性','グループ','VIP'].includes(pt.price_type) ? pt.price_type : 'other')}
                    onChange={e => {
                      if (e.target.value === 'other') {
                        handlePriceTypeChange(idx, j, 'price_type', '');
                      } else {
                        handlePriceTypeChange(idx, j, 'price_type', e.target.value);
                      }
                    }}
                  >
                    <option value="通常">{t('optionPriceTypeNormal')}</option>
                    <option value="男性">{t('optionPriceTypeMale')}</option>
                    <option value="女性">{t('optionPriceTypeFemale')}</option>
                    <option value="グループ">{t('optionPriceTypeGroup')}</option>
                    <option value="VIP">{t('optionPriceTypeVIP')}</option>
                    <option value="other">{t('optionPriceTypeOther')}</option>
                  </select>
                  {/* "その他"または空文字列のときは必ずテキスト入力欄を表示 */}
                  {(pt.price_type === '' || !['通常','男性','女性','グループ','VIP'].includes(pt.price_type)) && (
                    <>
                      <input
                        type="text"
                        value={pt.price_type}
                        maxLength={20}
                        onChange={e => {
                          const val = e.target.value;
                          // <, > 禁止
                          if (/[<>]/.test(val)) return;
                          handlePriceTypeChange(idx, j, 'price_type', val);
                        }}
                        placeholder={t('placeholderPriceTypeInput')}
                        className={styles.inlineTypeInput}
                      />
                      {pt.price_type.length > 0 && /[<>]/.test(pt.price_type) && (
                        <span className={styles.inlineErrorText}>{t('noteTypeInputForbidden')}</span>
                      )}
                    </>
                  )}
                </label>
                <label>
                  {t('priceAmountLabel')}
                  <input type="number" value={pt.price} onChange={e => handlePriceTypeChange(idx, j, 'price', e.target.value)} placeholder={t('placeholderPrice')} />
                </label>
                {pt.price_type === 'グループ' && (
                  <label>
                    {t('priceGroupCountLabel')}
                    <input type="number" value={pt.min_people || ''} onChange={e => handlePriceTypeChange(idx, j, 'min_people', e.target.value)} placeholder={t('placeholderGroupCount')} />
                  </label>
                )}
                <label>
                  {t('priceSaleLimitLabel')}
                  <input type="number" value={pt.ticket_limit} onChange={e => handlePriceTypeChange(idx, j, 'ticket_limit', e.target.value)} placeholder={t('placeholderUnlimited')} />
                </label>
                <div className={styles.priceTypeActions}>
                  <button type="button" onClick={() => removePriceType(idx, j)} className={styles.dangerButton}>{t('priceTypeDelete')}</button>
                </div>
              </div>
            ))}
            <div className={styles.priceTypeActions}>
              <button type="button" onClick={() => addPriceType(idx, '')} className={styles.secondaryButton}>{t('addPriceType')}</button>
            </div>
          </div>
        </div>
      ))}
      <button type="button" onClick={addTicketPrice} className={styles.secondaryButton}>{t('addTicketGroup')}</button>
        </>
      )}
    </fieldset>
    
<fieldset>
  <legend>{t('legendArtists')}</legend>

  {/* 出演者グループ管理 */}
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span style={{ fontWeight: 600, fontSize: 14 }}>出演者グループ（任意）</span>
      <button type="button"
        style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, border: '1px solid #bbb', background: '#f9fafb', cursor: 'pointer' }}
        onClick={() => {
          const key = crypto.randomUUID();
          setPerformerGroups(prev => [...prev, { key, id: null, name: '', sort_order: prev.length }]);
        }}>
        ＋ グループを追加
      </button>
    </div>
    {performerGroups.length > 0 && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {performerGroups.map((g, gi) => (
          <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="text" value={g.name} placeholder="グループ名（例: DAY1, LIVE）"
              style={{ flex: 1, padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db' }}
              onChange={e => setPerformerGroups(prev => prev.map((x, i) => i === gi ? { ...x, name: e.target.value } : x))} />
            <button type="button"
              style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}
              onClick={() => {
                setPerformerGroups(prev => prev.filter((_, i) => i !== gi));
                setArtists(prev => prev.map(a => a.group_key === g.key ? { ...a, group_key: null } : a));
              }}>
              削除
            </button>
          </div>
        ))}
      </div>
    )}
  </div>

  <div className={styles.artistGuideBox}>
    <strong>{t('noteArtistGuideRegistered')}</strong>{t('noteArtistGuideText1')}<br />
    <strong>{t('noteArtistGuideUnregistered')}</strong>{t('noteArtistGuideText2')}
  </div>

  {/* artist search */}
  <label>
    {t('labelArtistSearch')}
    <span style={{ fontSize: '12px', color: '#888', display: 'block', marginTop: '2px' }}>
      {t('noteArtistSearch')}
    </span>
    <input
      type="text"
      value={artistSearch}
      onChange={(e) => setArtistSearch(e.target.value)}
      placeholder={t('placeholderArtistSearch')}
    />
  </label>
  <p className={styles.artistHint}>{t('noteArtistHint')}</p>

  {/* 候補一覧 */}
  {artistSearch && artistResults.length > 0 && (
    <ul className={styles.searchResults}>
      {artistResults.map((a) => {
        const imgUrl = a.profile_image_url || null;
        const initials = (a.organization_name || '').trim().charAt(0).toUpperCase();
        return (
          <li
            key={a.id}
            onClick={() => setSelectedArtist({ id: a.id, name: a.organization_name })}
            style={{ cursor: 'pointer' }}
          >
            <div className={styles.searchResultRow}>
              <div className={styles.artistResultItem}>
                {imgUrl ? (
                  <img src={imgUrl} alt={`${a.organization_name} avatar`} className={styles.artistAvatar} />
                ) : (
                  <span className={styles.artistAvatar} aria-hidden="true">{initials || 'A'}</span>
                )}
                <span>{a.organization_name}</span>
              </div>
              <button
                type="button"
                className={styles.searchAddButton}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addArtistToList({ id: a.id, name: a.organization_name });
                  setSelectedArtist(null);
                  setArtistSearch('');
                  setArtistResults([]);
                }}
              >
                {t('artistSearchAdd')}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  )}

  {/* 選択されたらボタンで追加 */}
  {selectedArtist && (
    <div className={styles.selectedArtistBox}>
      <p className={styles.selectedArtistText}>{t('artistSelectedName', { name: selectedArtist.name })}</p>
      <button
        className={styles.secondaryButton}
        type="button"
        onClick={() => {
          addArtistToList(selectedArtist);
          setSelectedArtist(null);
          setArtistSearch('');
          setArtistResults([]);
        }}
      >
        {t('artistAddSelected')}
      </button>
    </div>
  )}

  {/* 現在のリスト表示 */}
  {artists.map((artist, i) => (
  <div key={i} className={styles.artistRow}>
    <label className={styles.artistRowLabel}>
      <span className={styles.artistRowHeader}>
        <span className={`${styles.artistRowTitle}`}>
        {t('artistNumber', { num: i + 1 })}
      </span>
        <span className={`${styles.artistModeBadge} ${artist.id ? styles.artistModeRegistered : styles.artistModeManual}`}>
          {artist.id ? t('artistBadgeRegistered') : t('artistBadgeManual')}
        </span>
      </span>
      <input
        type="text"
        value={artist.name}
        onChange={(e) => handleArtistValueChange(i, 'name', e.target.value)}
        placeholder={artist.id ? t('placeholderArtistRegistered') : t('placeholderArtistManual')}
      />
    </label>
    {performerGroups.length > 0 && (
      <label style={{ fontSize: 13, marginBottom: 4, display: 'block' }}>
        グループ
        <select value={artist.group_key || ''}
          onChange={e => handleArtistValueChange(i, 'group_key', e.target.value || null)}
          style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db' }}>
          <option value="">なし</option>
          {performerGroups.filter(g => g.name).map(g => (
            <option key={g.key} value={g.key}>{g.name}</option>
          ))}
        </select>
      </label>
    )}
    <button
      className={styles.dangerButton}
      type="button"
      onClick={() => {
        const updated = [...artists];
        updated.splice(i, 1);
        setArtists(updated);
      }}
    >
      {t('artistDelete')}
    </button>
    {!artist.id && artist.name.trim() !== '' && (
      <div className={styles.artistExtraFields}>
        <div className={styles.artistExtraHint}>
          {t('noteArtistExtraHint')}
        </div>
        <label className={styles.artistRowLabel}>
          <span className={styles.artistRowTitle}>{t('artistDescTitle')}</span>
          <textarea
            value={artist.description || ''}
            onChange={(e) => handleArtistValueChange(i, 'description', e.target.value)}
            placeholder={t('placeholderArtistDesc')}
            maxLength={2000}
          />
        </label>
        <label className={styles.artistRowLabel}>
          <span className={styles.artistRowTitle}>{t('artistPhotoTitle')}</span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleArtistImageChange(i, e.target.files?.[0] || null)}
          />
        </label>
        {(artist.imagePreview || artist.existingImageUrl) && (
          <img
            src={artist.imagePreview || artist.existingImageUrl}
            alt={`${artist.name || 'artist'} preview`}
            className={styles.artistImagePreview}
          />
        )}
      </div>
    )}
    {!artist.id && artist.name.trim() === '' && (
      <div className={styles.artistExtraCollapsed}>
        {t('noteArtistCollapsed')}
      </div>
    )}
  </div>
))}
  <div className={styles.artistActionRow}>
    <button
      className={styles.secondaryButton}
      type="button"
      onClick={() => setArtists([...artists, createEmptyArtist()])}
    >
      {t('artistAdd')}
    </button>
  </div>
</fieldset>


          <fieldset>
            <legend>{t('legendDescription')}</legend>
            <span style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>
              {t('noteDescriptionLength')}<br />
              <span style={{ color: '#888' }}>
                {t('noteDescriptionChars')}
              </span>
            </span>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder={t('placeholderDescription')}
              maxLength={3000}
              autoComplete="off"
              className={getFieldError('description') ? styles.inputError : ''}
            />
            {getFieldError('description') && <p className={styles.fieldError}>{getFieldError('description')}</p>}
          </fieldset>

          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>{t('legendContact')}</legend>
            <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
              {t('noteContact')}
            </span>
            <textarea
              name="contact_info"
              value={form.contact_info}
              onChange={handleChange}
              placeholder={t('placeholderContact')}
              maxLength={1000}
              autoComplete="off"
              required
              className={getFieldError('contact_info') ? styles.inputError : ''}
            />
            {getFieldError('contact_info') && <p className={styles.fieldError}>{getFieldError('contact_info')}</p>}
          </fieldset>

          <div className={styles.submitArea}>
            {getErrorSummary() && <p className={styles.error}>{getErrorSummary()}</p>}
            {success && <p style={{ color: '#16a34a', margin: '0 0 8px' }}>{success}</p>}
            {draftNote && !success && (
              <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 8px' }}>{draftNote}</p>
            )}
            <div className={styles.submitButtons}>
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('btnSubmitting') : t('btnSubmit')}
              </button>
              <button
                type="button"
                className={styles.secondarySubmitButton}
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isSubmitting}
              >
                {isSavingDraft ? t('btnDrafting') : t('btnDraft')}
              </button>
            </div>
            {getFieldError('submit') && <p className={styles.fieldError}>{getFieldError('submit')}</p>}
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateEventPage;